import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from '@mui/material';
import { TaskStatus, UserData, SnackbarState } from '../../types/todoTypes';
import { createTask } from '../../services/todoService';
import { getUsers } from '../../services/authService';
import { validateTitle, validateDescription } from '../../utils/todoUtils';
import { useAuth } from '../../contexts/AuthContext';

interface CreateTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onTaskCreated: () => void;
  setSnackbar: (snackbar: SnackbarState) => void;
}

const CreateTaskDialog: React.FC<CreateTaskDialogProps> = ({
  open,
  onClose,
  onTaskCreated,
  setSnackbar,
}) => {
  const { currentUser } = useAuth();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<TaskStatus>('Pending');
  const [assignedTo, setAssignedTo] = useState('');
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [titleError, setTitleError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const fetchedUsers = await getUsers('', 100); // Fetch all users
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load users',
        severity: 'error',
      });
    } finally {
      setLoadingUsers(false);
    }
  }, [setSnackbar]);

  // Load users when dialog opens
  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open, loadUsers]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setStatus('Pending');
    setAssignedTo('');
    setTitleError('');
    setDescriptionError('');
  };

  const handleSubmit = async () => {
    // Validate inputs
    const titleValidation = validateTitle(title);
    const descriptionValidation = validateDescription(description);

    setTitleError(titleValidation.error || '');
    setDescriptionError(descriptionValidation.error || '');

    if (!titleValidation.valid || !descriptionValidation.valid) {
      return;
    }

    if (!assignedTo) {
      setSnackbar({
        open: true,
        message: 'Please select a user to assign the task to',
        severity: 'warning',
      });
      return;
    }

    if (!currentUser) {
      setSnackbar({
        open: true,
        message: 'User not authenticated',
        severity: 'error',
      });
      return;
    }

    setLoading(true);

    try {
      await createTask(
        {
          title: title.trim(),
          description: description.trim(),
          status,
          assignedTo,
        },
        currentUser.uid
      );

      setSnackbar({
        open: true,
        message: 'Task created successfully',
        severity: 'success',
      });

      onTaskCreated();
      handleClose();
    } catch (error) {
      console.error('Error creating task:', error);
      setSnackbar({
        open: true,
        message: 'Failed to create task',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Task</DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* User selection dropdown */}
          <FormControl fullWidth>
            <InputLabel id="user-select-label">Assign To *</InputLabel>
            <Select
              labelId="user-select-label"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              label="Assign To *"
              disabled={loadingUsers}
            >
              {loadingUsers ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading users...
                </MenuItem>
              ) : users.length === 0 ? (
                <MenuItem disabled>No users available</MenuItem>
              ) : (
                users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* Title input */}
          <TextField
            label="Task Title *"
            fullWidth
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleError('');
            }}
            error={!!titleError}
            helperText={titleError || 'Min 3 characters, max 100 characters'}
            autoFocus
          />

          {/* Description input */}
          <TextField
            label="Description *"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setDescriptionError('');
            }}
            error={!!descriptionError}
            helperText={descriptionError || 'Min 10 characters, max 1000 characters'}
          />

          {/* Status selection */}
          <FormControl fullWidth>
            <InputLabel id="status-select-label">Status *</InputLabel>
            <Select
              labelId="status-select-label"
              value={status}
              onChange={(e) => setStatus(e.target.value as TaskStatus)}
              label="Status *"
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Blocked">Blocked</MenuItem>
              <MenuItem value="Complete">Complete</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTaskDialog;
